import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo } from "@/components/feature/Seo";
import FigureCard from "@/components/feature/FigureCard";
import { categoryMeta } from "@/components/feature/categoryMeta";
import { figures } from "@/mocks/figures";

const origin = "https://focuspeople.example";

export default function FigureDetail() {
  const { id } = useParams();
  const figure = figures.find((f) => f.id === Number(id));

  useSeo({
    title: figure
      ? `${figure.name}｜最新爆料人物追踪`
      : "未找到档案｜最新爆料人物追踪",
    description: figure
      ? figure.summary.slice(0, 90)
      : "最新爆料人物追踪，该人物档案暂未找到或已更新。",
    keywords: figure ? figure.keywords.join(",") : "最新爆料,人物追踪",
    jsonLd: figure
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: figure.name,
            description: figure.summary,
            author: { "@type": "Organization", name: "焦点人物志" },
            publisher: { "@type": "Organization", name: "焦点人物志" },
            datePublished: `2026-${figure.published.replace("-", "-")}T09:00:00+08:00`,
            mainEntityOfPage: `${origin}/renwu/${figure.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: `${origin}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "人物档案库",
                item: `${origin}/renwu`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: figure.name,
                item: `${origin}/renwu/${figure.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!figure) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <i className="ri-user-unfollow-line text-5xl text-secondary-500"></i>
          <h1 className="mt-4 font-display text-xl font-bold text-foreground-950">
            未找到该人物档案
          </h1>
          <Link
            to="/renwu"
            className="mt-4 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
          >
            返回档案库
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const meta = categoryMeta(figure.category);
  const related = figures
    .filter((f) => f.id !== figure.id)
    .sort(
      (a, b) =>
        (b.category === figure.category ? 1 : 0) -
        (a.category === figure.category ? 1 : 0),
    )
    .slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 pt-8 md:px-6">
          <nav
            aria-label="面包屑"
            className="flex flex-wrap items-center gap-1.5 text-xs text-secondary-700"
          >
            <Link to="/" className="whitespace-nowrap hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to="/renwu"
              className="whitespace-nowrap hover:text-primary-700"
            >
              人物档案库
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-950">{figure.name}</span>
          </nav>

          <header className="mt-6 grid grid-cols-1 gap-6 border-b border-background-200 pb-8 lg:grid-cols-[auto_1fr] lg:items-start">
            <span
              className={`flex h-20 w-20 shrink-0 items-center justify-center font-display text-3xl font-bold ${meta.avatar}`}
            >
              {figure.initial}
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-secondary-900">
                  {meta.label}
                </span>
                <span className="rounded-full bg-foreground-950 px-2.5 py-1 text-xs font-medium text-background-50">
                  {figure.status}
                </span>
                <span className="text-xs text-secondary-700">
                  最近更新 {figure.published}
                </span>
              </div>
              <h1 className="mt-3 font-display text-3xl font-black text-foreground-950 md:text-4xl">
                {figure.name}
              </h1>
              <p className="mt-1 text-sm font-medium text-secondary-700">
                {figure.role}
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {figure.tagline}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {figure.keywords.map((k) => (
                  <span
                    key={k}
                    className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-xs text-secondary-900"
                  >
                    # {k}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="grid grid-cols-1 gap-8 py-8 lg:grid-cols-[1fr_320px]">
            <div>
              <section>
                <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-950">
                  <span className="inline-block h-5 w-1 bg-primary-500"></span>
                  档案概述
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {figure.summary}
                </p>
              </section>

              <section className="mt-8">
                <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-950">
                  <span className="inline-block h-5 w-1 bg-primary-500"></span>
                  追踪详情
                </h2>
                <div className="mt-4 space-y-4">
                  {figure.story.map((p) => (
                    <p
                      key={p.slice(0, 12)}
                      className="text-sm leading-relaxed text-foreground-700 md:text-base"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </section>

              <section className="mt-8">
                <h2 className="flex items-center gap-2 font-display text-xl font-bold text-foreground-950">
                  <span className="inline-block h-5 w-1 bg-primary-500"></span>
                  进展时间线
                </h2>
                <ol className="mt-4 space-y-4">
                  {figure.timeline.map((t) => (
                    <li
                      key={t.title}
                      className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
                    >
                      <span className="shrink-0 font-display text-sm font-bold text-primary-600">
                        {t.date}
                      </span>
                      <div>
                        <h3 className="font-display text-base font-bold text-foreground-950">
                          {t.title}
                        </h3>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                          {t.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>

            <aside className="space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-display text-base font-bold text-foreground-950">
                  关键信息
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-secondary-700">档案角色</dt>
                    <dd className="text-right text-foreground-950">
                      {figure.role}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-secondary-700">所属分类</dt>
                    <dd className="text-right text-foreground-950">
                      {meta.label}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-secondary-700">当前状态</dt>
                    <dd className="text-right font-medium text-primary-700">
                      {figure.status}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-background-200 pt-3">
                    <dt className="text-secondary-700">阅读时长</dt>
                    <dd className="text-right text-foreground-950">
                      约 {figure.minutes} 分钟
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-lg bg-accent-100 p-5">
                <h2 className="font-display text-base font-bold text-accent-900">
                  追踪立场提醒
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-accent-900/90">
                  本文以人物追踪与信息梳理为目的。文中「涉事、被点名」等表述均指报道中出现的角色，不代表已经司法或权威认定，请以官方后续结论为准。
                </p>
              </div>

              <div className="rounded-lg border border-background-200 p-5">
                <h2 className="font-display text-base font-bold text-foreground-950">
                  关联与网络
                </h2>
                <ul className="mt-4 space-y-3">
                  {figure.relations.map((r) => (
                    <li
                      key={r.name}
                      className="rounded-md bg-background-100 px-3 py-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground-950">
                          {r.name}
                        </span>
                        <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                          {r.type}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                        {r.note}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>

          <section className="border-t border-background-200 py-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-foreground-950">
                相关人物档案
              </h2>
              <Link
                to="/renwu"
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-700 hover:text-primary-600"
              >
                更多档案
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((f) => (
                <FigureCard key={f.id} figure={f} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}